import { Board as BoardType, Direction, GameStatus, Position, PuzzleType } from '../types'
import { clearDaily5State, loadDaily5State, saveDaily5State } from '../controllers/state'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { getPlayed, setResult } from '../controllers/stats'
import { randomFromArray } from '../util/random'
import { setArchiveResult } from '../controllers/archive'
import { useInterval } from 'react-use'
import { useSettings } from '../hooks/useSettings'
import { useTimings } from '../hooks/useTimings'
import $, { Cash, Selector } from 'cash-dom'
import Board from './Board'
import Button from './Button'
import calculateDirection from '../util/calculateDirection'
import Clock from './Clock'
import clx from '../util/clx'
import CounterBar from './CounterBar'
import GameAdBox from './GameAdBox'
import GameHelp from './GameHelp'
import getPointerPosition from '../util/getPointerPosition'
import messages from '../../config/messages.json'
import PostGameMessage from './PostGameMessage'
import PostGameStats from './PostGameStats'
import reverse from '../util/reverse'
import Scoring from './Scoring'
import scrollLockOptions from '../util/scrollLockOptions'
import Timecard from './Timecard'
import {
	ANIMATION_DURATION,
	GAME_IDS,
	INCORRECT_FREEZE_DURATION,
	INITIAL_INSTRUCTION_DELAY,
	NEW_INSTRUCTION_DELAY,
	TOTAL_WORDS,
} from '../config'
import ArchivePromoCard from './ArchivePromoCard'

type GameProps = {
	openModal: (modalName: string) => void
	puzzleType: PuzzleType
	puzzleDate: string
	puzzleNumber: number
}

const Game: FunctionComponent<GameProps> = props => {
	const { openModal, puzzleType, puzzleNumber, puzzleDate } = props

	const [status, setStatus] = useState<GameStatus>('loading')
	const [showHelp, setShowHelp] = useState(false)
	const [time, setTime] = useState(0)
	const [wordsRemaining, setWordsRemaining] = useState<string[]>([])

	const boardRef = useRef<BoardType>([[]])
	const busyRef = useRef<boolean>(false)
	const rootRef = useRef<HTMLDivElement>(null)
	const selectedWordTimeoutRef = useRef<number | null>(null)
	const selectionDirectionRef = useRef<Direction | null>(null)
	const showHelpTimeoutRef = useRef<number | null>(null)
	const startLetterRef = useRef<Cash | null>(null)
	const startPosRef = useRef<Position | null>(null)

	const settings = useSettings()
	const showClock = settings.getShowClock()
	const isArchive = puzzleType === 'daily5-archive'
	const gameID = isArchive ? GAME_IDS.DAILY5_ARCHIVE : GAME_IDS.DAILY5
	const timings = useTimings(gameID)
	const boardSize = 5

	/**
	 * Populate the selectedWord and wordMessage elements with the given word
	 * and message. Hide them after 4 seconds
	 */
	const displaySelectedWord = (word: string, message: string) => {
		const selectedWord = $(rootRef.current).find('.selected-word')
		const wordMessage = $(rootRef.current).find('.word-message')

		selectedWord.addClass('selected-word--visible').text(word)
		wordMessage.addClass('word-message--visible').text(message)

		const selectedWordTimeout = selectedWordTimeoutRef.current
		if (selectedWordTimeout) {
			clearTimeout(selectedWordTimeout)
		}
		selectedWordTimeoutRef.current = setTimeout(() => {
			selectedWord.removeClass('selected-word--visible')
			wordMessage.removeClass('word-message--visible')
		}, 4000)
	}

	const onComplete = () => {
		if (status === 'complete1' || status === 'complete2') {
			return
		}
		clearAllBodyScrollLocks()
		setStatus('complete1')
		setShowHelp(false)
		setTimeout(() => {
			setStatus('complete2')
		}, 1000)
	}

	const onContinue = () => {
		// If they've found one or more words, move from 'ready' to 'playing'
		if (wordsRemaining.length < TOTAL_WORDS) {
			setStatus('playing')
		} else {
			setStatus('ready')
		}
	}

	const setupBoard = () => {
		const $board = $(rootRef.current).find('.board')
		const board = boardRef.current
		for (let x = 0; x < board.length; x++) {
			const col = $(`<div class="board__col"></div>`)
			for (let y = Math.max(...board.map(c => c.length)) - 1; y >= 0; y--) {
				if (!board[x][y]) continue
				const letter = $(
					`<div class="letter"><div class="letter__inner">${
						board[x][y] || ''
					}</div></div>`
				)
				letter.data('pos', { x, y })
				col.append(letter)
			}
			$board.append(col)
		}
	}

	const teardownBoard = () => {
		const $board = $(rootRef.current).find('.board')
		$board.children('.board__col').remove()
	}

	const loadPuzzle = useCallback(async () => {
		const state = await loadDaily5State(puzzleDate, isArchive)
		const { board, time } = state
		boardRef.current = board
		if (showHelpTimeoutRef.current) {
			clearTimeout(showHelpTimeoutRef.current)
		}
		showHelpTimeoutRef.current = setTimeout(() => setShowHelp(true), INITIAL_INSTRUCTION_DELAY)

		setWordsRemaining(state.wordsRemaining)
		setTime(time)
		setupBoard()

		const gameHasBeenCompleted = state.wordsRemaining.length === 0
		if (gameHasBeenCompleted) {
			setStatus('complete0')
			setTimeout(onComplete, 1000)
		} else {
			const gameHasBeenStarted =
				state.wordsRemaining.length > 0 && state.wordsRemaining.length < TOTAL_WORDS
			setStatus(gameHasBeenStarted ? 'paused' : 'ready')
			if (!gameHasBeenStarted) {
				// This is a fresh attempt on a puzzle, clear any stale timings
				timings.reset()
			}
		}

		const boardElement = rootRef.current?.querySelector('.board')
		if (boardElement) {
			disableBodyScroll(boardElement, scrollLockOptions)
		}
	}, [puzzleDate])

	const onReset = useCallback(() => {
		teardownBoard()
		clearDaily5State(isArchive)
		loadPuzzle()
	}, [loadPuzzle])

	// Initialize puzzle
	useEffect(() => {
		loadPuzzle()
		return teardownBoard
	}, [loadPuzzle])

	// Attach all the event listeners
	useEffect(() => {
		const $board = $(rootRef.current).find('.board')
		$board.on('mousedown touchstart', '.letter', handlePointerDown)
		$board.on('mouseenter pointerenter', '.letter', handlePointerEnter)
		$board.on('mouseleave touchleave', '.letter', handlePointerLeave)
		$('body').on('mousemove touchmove', handlePointerMove)
		$('body').on('mouseup touchend', handlePointerUp)

		return () => {
			$board.off('mousedown touchstart', '.letter', handlePointerDown)
			$board.off('mouseenter pointerenter', '.letter', handlePointerEnter)
			$board.off('mouseleave touchleave', '.letter', handlePointerLeave)
			$('body').off('mousemove touchmove', handlePointerMove)
			$('body').off('mouseup touchend', handlePointerUp)
		}
	}, [wordsRemaining, time])

	// Update clock every second and save state to storage
	useInterval(() => {
		if (status === 'playing' && wordsRemaining.length > 0) {
			setTime(time + 1)
			saveDaily5State(
				{
					board: boardRef.current,
					puzzleId: puzzleDate,
					time,
					wordsRemaining,
				},
				isArchive
			)
		}
	}, 1000)

	// Pause the clock on visibility change
	useEffect(() => {
		const onVisibilityChanged = () => {
			const numWordsRemaining = wordsRemaining.length
			const gameInProgress = numWordsRemaining > 0 && numWordsRemaining < TOTAL_WORDS
			if (document.hidden && gameInProgress) setStatus('paused')
		}
		document.addEventListener('visibilitychange', onVisibilityChanged)
		return () => document.removeEventListener('visibilitychange', onVisibilityChanged)
	}, [])

	// We need to pass the total time through to the Board element so that it
	// can render the post-game score and messages once the game has finished,
	// but we don't want to cause the Board to re-render every time the clock
	// changes.
	// So, we'll keep the time static (0) while the game is in progress, and
	// then swap in the actual time value when it's in any other state.
	const totalTime = status !== 'playing' ? time : 0
	const gameClasses = clx('game', `game--${status}`, { 'game--showhelp': showHelp })

	return (
		<div ref={rootRef} className={gameClasses}>
			<div className="game__top">
				<div className="selected-word" />
				<div className="word-message" />
				{getPlayed() === 0 && <GameHelp numWordsRemaining={wordsRemaining.length} />}
			</div>
			<div className="game__middle">
				<Board
					cols={boardSize}
					isArchive={isArchive}
					puzzleNumber={puzzleNumber}
					resetGame={onReset}
					totalTime={totalTime}
				/>
				<Button name="continue" onClick={onContinue}>
					Continue
				</Button>
			</div>
			<div className="game__bottom">
				<CounterBar numWordsRemaining={wordsRemaining.length} />
				{showClock && <Clock time={time} />}
				<small className="game__instructions-reminder">
					The clock will begin when you find the first word.
				</small>
			</div>
			<Timecard gameId={gameID} />
			{!isArchive && <PostGameMessage openModal={openModal} />}
			<GameAdBox />
			{!isArchive && <PostGameStats />}
			{!isArchive && <ArchivePromoCard />}
			<Scoring />
		</div>
	)

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	function handlePointerDown(e: MouseEvent | TouchEvent) {
		const startLetter = $(e.currentTarget as Selector)
		$('.letter').removeClass('letter--selected, letter-highlighted')
		startLetter.addClass('letter--selected').addClass('letter--highlighted')
		startLetterRef.current = startLetter
		startPosRef.current = getPointerPosition(e)
	}

	function handlePointerMove(e: MouseEvent | TouchEvent) {
		const startPos = startPosRef.current
		const startLetter = startLetterRef.current

		if (startPos === null || startLetter === null) return

		const newPos = getPointerPosition(e)

		// Calculate distance from startPos
		const dist = Math.sqrt((newPos.x - startPos.x) ** 2 + (newPos.y - startPos.y) ** 2)

		// No significant movement
		if (dist < 20) return

		// returns a direction of either 'u', 'd', 'l', 'r', 'ul', 'ur', 'dl', or 'dr'
		const selectionDirection = calculateDirection(startPos, newPos)
		selectionDirectionRef.current = selectionDirection

		const $board = $(rootRef.current).find('.board')
		$board.find('.letter').each((i, letter) => {
			let selected = false
			const letterPos = $(letter).data('pos')
			if (!letterPos) return

			const startLetterPos = startLetter.data('pos')
			if (selectionDirection === 'u' || selectionDirection === 'd') {
				selected =
					letterPos.x === startLetterPos.x &&
					letterPos.y < boardSize &&
					letterPos.y < boardSize
			}
			if (selectionDirection === 'r' || selectionDirection === 'l') {
				selected = letterPos.y === startLetterPos.y
			}
			if (selectionDirection === 'ur' || selectionDirection === 'dl') {
				selected =
					letterPos.x - letterPos.y === startLetterPos.x - startLetterPos.y &&
					letterPos.y < boardSize
			}
			if (selectionDirection === 'ul' || selectionDirection === 'dr') {
				selected =
					letterPos.x + letterPos.y === startLetterPos.x + startLetterPos.y &&
					letterPos.y < boardSize
			}
			$(letter).toggleClass('letter--highlighted', selected)
		})
	}

	function handlePointerEnter(e: MouseEvent | TouchEvent) {
		const $target = $(e.target as Selector)
		if (startLetterRef.current && $target.hasClass('letter--highlighted')) {
			$target.addClass('letter--selected')
		}
	}

	function handlePointerLeave(e) {
		const $target = $(e.target as Selector)
		if (!startLetterRef.current || !$target.is(startLetterRef.current)) {
			$target.removeClass('letter--selected')
		}
	}

	function handlePointerUp() {
		const selectionDirection = selectionDirectionRef.current

		if (busyRef.current || !selectionDirection) return

		const letters = $('.letter.letter--highlighted')
		let letterElemArr = letters.get()

		const reverseDirections = ['u', 'ul', 'dl', 'l']

		if (reverseDirections.includes(selectionDirection)) {
			letterElemArr = reverse(letterElemArr)
		}

		const selectedWord = letterElemArr.map(letter => $(letter).text()).join('')

		const wordToGet = wordsRemaining[wordsRemaining.length - 1] || ''

		startLetterRef.current = null

		// If the player has made an invalid guess (i.e, they've diagonally
		// selected a 4-letter word on a 5-letter board)
		if (!selectedWord || !wordToGet || selectedWord.length !== wordToGet.length) {
			letters.removeClass('letter--selected')
			letters.removeClass('letter--highlighted')
			return
		}

		// The player has made a valid guess...
		const $board = $(rootRef.current).find('.board')
		$board.addClass('board--frozen')
		busyRef.current = true

		// After any valid guess, hide the help and show it again later
		setShowHelp(false)
		if (showHelpTimeoutRef.current) {
			clearTimeout(showHelpTimeoutRef.current)
		}
		showHelpTimeoutRef.current = setTimeout(() => setShowHelp(true), NEW_INSTRUCTION_DELAY)

		// If the player has found the correct word...
		if (selectedWord === wordToGet) {
			const updatedWordsRemaining = [...wordsRemaining]
			updatedWordsRemaining.pop()
			setWordsRemaining(updatedWordsRemaining)

			const correctMessage = randomFromArray(messages.correct)
			displaySelectedWord(selectedWord, correctMessage)

			letterElemArr.forEach((letter, i) => {
				const delay = i * (ANIMATION_DURATION / letterElemArr.length)
				const $letter = $(letter)
				setTimeout(() => {
					$letter
						.addClass('letter--correct')
						.removeClass('letter--selected, letter--highlighted')
				}, delay)

				if (['u', 'd'].includes(selectionDirection)) {
					setTimeout(() => $letter.addClass('letter--hidden'), ANIMATION_DURATION * 2)
				} else {
					setTimeout(() => $letter.addClass('letter--hidden'), delay + ANIMATION_DURATION)
				}
			})

			// Remove the letters from the HTML completely after they have been
			// animated away
			const removeWord = () => {
				letters.each((i, letter) => {
					const otherLettersInCol = $(letter).prevAll()
					// Update the "pos" for the letters above the removed one
					otherLettersInCol.each((p, prevLetter) => {
						const data = $(prevLetter).data('pos')
						$(prevLetter).data('pos', { x: data.x, y: data.y - 1 })
					})
					$(letter).remove()
				})
				$board.removeClass('board--frozen')
				busyRef.current = false
			}

			const totalAnimationTime = ANIMATION_DURATION * 4
			setTimeout(removeWord, totalAnimationTime)

			// update the board variable to store in state
			let newBoard: BoardType = [...boardRef.current.map(col => [...col])]
			// remove the letters from the board
			// replace them with blank strings...
			letters.each((i, letter) => {
				const { x, y } = $(letter).data('pos')
				newBoard[x][y] = ''
			})
			// and then remove all blank letters from the arrays
			newBoard = newBoard.map(col => col.filter(l => l !== ''))
			boardRef.current = newBoard

			// If the game is in 'ready' state, and they have found the first word,
			// we can start the clock
			if (updatedWordsRemaining.length < TOTAL_WORDS && updatedWordsRemaining.length > 0) {
				setStatus('playing')
			}

			const wordsSolved = TOTAL_WORDS - updatedWordsRemaining.length
			timings.setWordTiming(wordsSolved - 1, selectedWord, time)

			saveDaily5State(
				{
					board: boardRef.current,
					wordsRemaining: updatedWordsRemaining,
					puzzleId: puzzleDate,
					time,
				},
				isArchive
			)

			// Save the player results if they've finished the game
			if (updatedWordsRemaining.length === 0) {
				if (puzzleType === 'daily5') {
					// Save the daily result
					setResult({ i: puzzleNumber, time, sync: false })

					// Save to local archive results too, omitting the sync to
					// the server as it is not an archive attempt
					setArchiveResult({ i: puzzleNumber, time })
				} else if (puzzleType === 'daily5-archive') {
					// Save the archive result, and sync the attempt to server
					setArchiveResult({ i: puzzleNumber, time, sync: false })
				}
				setTimeout(onComplete, 1000)
			}

			// We’re done here, finish early
			return
		}

		// The player found an incorrect word...
		letters.addClass('letter--incorrect')
		letters.removeClass('letter--selected')
		letters.removeClass('letter--highlighted')

		const incorrectMessage = randomFromArray(
			messages.incorrect.filter(t => t !== $('.word-message').text())
		)
		displaySelectedWord(selectedWord, incorrectMessage)

		setTimeout(() => {
			letters.removeClass('letter--incorrect')
			$board.removeClass('board--frozen')
			busyRef.current = false
		}, INCORRECT_FREEZE_DURATION)
	}
}

export default Game
